import React, { useState } from "react";
import { addDays, addMonths, addYears, format } from "date-fns";
import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import ForfeitureAIDialog from "./ForfeitureAIDialog";
import { cn } from "../../components/shared/Card";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { HStack, VStack } from "../../components/utils";
import { resolveExitOutcome } from "../../utils/forfeitureUtils";
import {
  AddPlanReq,
  ActiveEmployeeExpiry,
  DualDateGuard,
  EmployeeExitType,
  ExerciseWindow,
  ExerciseWindowUnit,
  ExitDateBasis,
  ExitForfeitureRule,
  ExpiryMode,
  ForfeitureSettings,
} from "../../types/EsopPlan";

// ─── Constants ────────────────────────────────────────────────────────────────

export const EXIT_TYPE_LABELS: Record<EmployeeExitType, string> = {
  [EmployeeExitType.RESIGNATION]: "Resignation",
  [EmployeeExitType.TERMINATION]: "Termination",
  [EmployeeExitType.RETIREMENT]: "Retirement",
  [EmployeeExitType.PERFORMANCE_RELATED]: "Performance Related",
  [EmployeeExitType.MISCONDUCT]: "Misconduct",
  [EmployeeExitType.PERMANENT_DISABILITY]: "Permanent Disability",
  [EmployeeExitType.DEATH]: "Death",
  [EmployeeExitType.ABANDONMENT]: "Abandonment",
};

const ALL_EXIT_TYPES = Object.values(EmployeeExitType);

const UNIT_OPTIONS = [
  { value: ExerciseWindowUnit.DAYS, text: "Days" },
  { value: ExerciseWindowUnit.MONTHS, text: "Months" },
  { value: ExerciseWindowUnit.YEARS, text: "Years" },
];

const EXPIRY_MODE_OPTIONS = [
  {
    value: ExpiryMode.GRANT_EXPIRY,
    label: "Grant Date",
    description: "Expiry calculated from original grant issue date.",
    showWindow: true,
    showDate: false,
  },
  {
    value: ExpiryMode.FROM_VESTING_DATE,
    label: "Vesting Date",
    description: "Window starts from each individual vesting tranche.",
    showWindow: true,
    showDate: false,
  },
  {
    value: ExpiryMode.NEVER,
    label: "Never",
    description: "Grants do not expire while employee is active.",
    showWindow: false,
    showDate: false,
  },
  {
    value: ExpiryMode.SPECIFIC_DATE,
    label: "Specific Date",
    description: "Fixed hard stop date for all grants in this plan.",
    showWindow: false,
    showDate: true,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function defaultExitRule(exitType: EmployeeExitType): ExitForfeitureRule {
  return {
    exitType,
    vestedOptions: {
      exerciseWindow: { value: 90, unit: ExerciseWindowUnit.DAYS },
      onExpiry: "LAPSE",
    },
    unvestedOptions: {
      forfeitImmediately: true,
    },
  };
}

function defaultConditionalRule(): NonNullable<
  ExitForfeitureRule["conditionalRules"]
>[number] {
  return {
    vestedOptions: {
      exerciseWindow: { value: 90, unit: ExerciseWindowUnit.DAYS },
      onExpiry: "LAPSE" as const,
    },
    // No unvestedOptions — inherits from the base exit rule
  };
}

function getConditionalRuleSummary(rule: ExitForfeitureRule) {
  const conditionalRules = rule.conditionalRules ?? [];
  if (conditionalRules.length === 0) {
    return "No tenure-based overrides configured.";
  }

  if (conditionalRules.length === 1) {
    return "1 tenure-based override configured.";
  }

  return `${conditionalRules.length} tenure-based overrides configured.`;
}

// ─── Sub-component: Inline exercise window pill ───────────────────────────────

interface ExerciseWindowPillProps {
  window: ExerciseWindow;
  onChange: (w: ExerciseWindow) => void;
  disabled?: boolean;
}

function ExerciseWindowPill({
  window: ew,
  onChange,
  disabled,
}: ExerciseWindowPillProps) {
  return (
    <HStack className="items-center rounded-lg bg-gray-50 border border-gray-200 overflow-hidden w-fit">
      <input
        type="number"
        min="1"
        disabled={disabled}
        value={ew.value ?? ""}
        onChange={(e) => onChange({ ...ew, value: Number(e.target.value) })}
        className="w-20 text-center text-sm font-medium h-10 bg-transparent border-none outline-none px-3"
      />
      <div className="w-px h-6 bg-gray-200 shrink-0" />
      <Select
        disabled={disabled}
        options={UNIT_OPTIONS}
        valueGetter={(o) => o.value}
        textGetter={(o) => o.text}
        value={ew.unit}
        onChange={(e: any) =>
          onChange({ ...ew, unit: e.target.value as ExerciseWindowUnit })
        }
        className="border-none bg-transparent text-sm h-10 pr-8 pl-3"
      />
    </HStack>
  );
}

// ─── Sub-component: Unvested options fields ───────────────────────────────────

type UnvestedOpts = ExitForfeitureRule["unvestedOptions"];

interface UnvestedOptionsFieldsProps {
  value: UnvestedOpts;
  onChange: (v: UnvestedOpts) => void;
  disabled?: boolean;
}

function UnvestedOptionsFields({
  value,
  onChange,
  disabled,
}: UnvestedOptionsFieldsProps) {
  return (
    <VStack className="gap-3">
      {/* Default behavior callout */}
      {!value.acceleration?.enabled && (
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3">
          <Icon
            icon="mdi:information-outline"
            width={14}
            className="text-gray-400 shrink-0"
          />
          <span className="text-xs text-gray-500">
            Unvested options are{" "}
            <span className="font-semibold text-gray-700">
              lapsed immediately
            </span>{" "}
            on exit unless acceleration applies.
          </span>
        </div>
      )}

      {/* Acceleration */}
      <div className="rounded-lg  p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Vesting Acceleration
          </span>
          <SwitchButton
            label=""
            value={value.acceleration?.enabled ?? false}
            disabled={disabled}
            onClick={() =>
              onChange({
                ...value,
                acceleration: {
                  enabled: !(value.acceleration?.enabled ?? false),
                  percentage: value.acceleration?.percentage ?? 0,
                },
              })
            }
          />
        </div>
        {value.acceleration?.enabled && (
          <HStack className="items-center gap-2">
            <span className="text-xs text-gray-500 shrink-0">Accelerate</span>
            <HStack className="items-center rounded-lg bg-gray-50 border border-gray-200 overflow-hidden w-fit">
              <input
                type="number"
                min="0"
                max="100"
                disabled={disabled}
                value={value.acceleration.percentage}
                onChange={(e) =>
                  onChange({
                    ...value,
                    acceleration: {
                      enabled: true,
                      percentage: Math.min(
                        100,
                        Math.max(0, Number(e.target.value))
                      ),
                    },
                  })
                }
                className="w-16 text-center text-sm font-medium h-9 bg-transparent border-none outline-none px-3"
              />
              <div className="w-px h-5 bg-gray-200 shrink-0" />
              <span className="px-3 text-xs text-gray-400">%</span>
            </HStack>
            <span className="text-xs text-gray-400">of unvested options</span>
          </HStack>
        )}
      </div>
    </VStack>
  );
}

// ─── Sub-component: Single conditional rule card ──────────────────────────────

type ConditionalRule = NonNullable<
  ExitForfeitureRule["conditionalRules"]
>[number];

type TenureMode = "any" | "greater" | "between" | "less";

function getTenureMode(t?: {
  minMonths: number;
  maxMonths?: number;
}): TenureMode {
  if (!t) return "any";
  if (t.minMonths === 0 && t.maxMonths !== undefined) return "less";
  if (t.maxMonths !== undefined) return "between";
  return "greater";
}

const TENURE_MODE_OPTIONS = [
  { value: "any" as TenureMode, text: "any tenure" },
  { value: "greater" as TenureMode, text: "more than" },
  { value: "between" as TenureMode, text: "between" },
  { value: "less" as TenureMode, text: "less than" },
];

const TENURE_UNIT_OPTIONS = [
  { value: ExerciseWindowUnit.DAYS, text: "Days" },
  { value: ExerciseWindowUnit.MONTHS, text: "Months" },
  { value: ExerciseWindowUnit.YEARS, text: "Years" },
];

function tenureUnitLabel(unit?: ExerciseWindowUnit): string {
  switch (unit) {
    case ExerciseWindowUnit.DAYS:
      return "days";
    case ExerciseWindowUnit.YEARS:
      return "years";
    default:
      return "months";
  }
}

interface ConditionalRuleCardProps {
  rule: ConditionalRule;
  index: number;
  onChange: (r: ConditionalRule) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function ConditionalRuleCard({
  rule,
  index,
  onChange,
  onRemove,
  disabled,
}: ConditionalRuleCardProps) {
  const tenureMode = getTenureMode(rule.tenure);
  const ew = rule.vestedOptions?.exerciseWindow ?? {
    value: 90,
    unit: ExerciseWindowUnit.DAYS,
  };
  const unvested = rule.unvestedOptions ?? { forfeitImmediately: true };

  const tenureUnit = rule.tenure?.unit ?? ExerciseWindowUnit.MONTHS;

  const setTenureMode = (mode: TenureMode) => {
    if (mode === "any") {
      onChange({ ...rule, tenure: undefined });
    } else if (mode === "greater") {
      onChange({
        ...rule,
        tenure: {
          minMonths: rule.tenure?.minMonths || 12,
          maxMonths: undefined,
          unit: tenureUnit,
        },
      });
    } else if (mode === "between") {
      onChange({
        ...rule,
        tenure: {
          minMonths: rule.tenure?.minMonths || 12,
          maxMonths: rule.tenure?.maxMonths || 24,
          unit: tenureUnit,
        },
      });
    } else {
      onChange({
        ...rule,
        tenure: {
          minMonths: 0,
          maxMonths: rule.tenure?.maxMonths || 24,
          unit: tenureUnit,
        },
      });
    }
  };

  const updateWindow = (w: ExerciseWindow) =>
    onChange({
      ...rule,
      vestedOptions: {
        exerciseWindow: w,
        onExpiry: rule.vestedOptions?.onExpiry ?? ("LAPSE" as const),
      },
    });

  const toggleAcceleration = () => {
    const enabled = !(unvested.acceleration?.enabled ?? false);
    onChange({
      ...rule,
      unvestedOptions: {
        ...unvested,
        acceleration: {
          enabled,
          percentage: unvested.acceleration?.percentage ?? 100,
        },
      },
    });
  };

  const setAccelerationPct = (pct: number) =>
    onChange({
      ...rule,
      unvestedOptions: {
        ...unvested,
        acceleration: {
          enabled: true,
          percentage: Math.min(100, Math.max(0, pct)),
        },
      },
    });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-800">
          Condition {index + 1}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            Remove
          </button>
        )}
      </div>

      {/* Tenure sentence */}
      <div className="flex items-center flex-wrap gap-x-2 gap-y-2 text-sm text-gray-700 mb-4">
        <span>When an employee has completed</span>
        <Select
          disabled={disabled}
          options={TENURE_MODE_OPTIONS}
          valueGetter={(o) => o.value}
          textGetter={(o) => o.text}
          value={tenureMode}
          onChange={(e: any) => setTenureMode(e.target.value as TenureMode)}
          className="h-10 w-36 border border-gray-200 rounded pl-2 pr-8 text-sm text-gray-700"
        />
        {tenureMode !== "any" && (
          <HStack className="items-center rounded-lg bg-gray-50 border border-gray-200 overflow-hidden w-fit">
            {tenureMode !== "less" && (
              <input
                type="number"
                min="0"
                disabled={disabled}
                value={rule.tenure?.minMonths ?? ""}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    tenure: {
                      ...rule.tenure!,
                      minMonths: Number(e.target.value),
                    },
                  })
                }
                className="w-16 text-center text-sm font-medium h-10 bg-transparent border-none outline-none px-3"
              />
            )}
            {tenureMode === "between" && (
              <>
                <div className="w-px h-6 bg-gray-200 shrink-0" />
                <span className="text-gray-400 text-sm px-2">to</span>
                <div className="w-px h-6 bg-gray-200 shrink-0" />
              </>
            )}
            {(tenureMode === "between" || tenureMode === "less") && (
              <input
                type="number"
                min={tenureMode === "between" ? rule.tenure?.minMonths ?? 0 : 0}
                disabled={disabled}
                value={rule.tenure?.maxMonths ?? ""}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    tenure: {
                      ...rule.tenure!,
                      maxMonths: Number(e.target.value),
                    },
                  })
                }
                className="w-16 text-center text-sm font-medium h-10 bg-transparent border-none outline-none px-3"
              />
            )}
            <div className="w-px h-6 bg-gray-200 shrink-0" />
            <Select
              disabled={disabled}
              options={TENURE_UNIT_OPTIONS}
              valueGetter={(o) => o.value}
              textGetter={(o) => o.text}
              value={tenureUnit}
              onChange={(e: any) =>
                onChange({
                  ...rule,
                  tenure: rule.tenure
                    ? {
                        ...rule.tenure,
                        unit: e.target.value as ExerciseWindowUnit,
                      }
                    : undefined,
                })
              }
              className="border-none bg-transparent text-sm h-10 pr-8 pl-3"
            />
          </HStack>
        )}
        <span className="text-gray-600">
          {tenureMode === "any" ? "regardless of tenure," : `of service,`}
        </span>
        <span>the following exceptions apply:</span>
      </div>

      {/* Override bullets */}
      <div className="space-y-3 pl-2">
        {/* Vested: exercise window */}
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm text-gray-700">
          <span className="text-gray-400 select-none">•</span>
          <span>Set exercise window to</span>
          <ExerciseWindowPill
            window={ew}
            onChange={updateWindow}
            disabled={disabled}
          />
          <span>window.</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component: Clamp Against Other Exit Date ─────────────────────────────

function addWindow(date: Date, window: ExerciseWindow): Date {
  if (window.unit === ExerciseWindowUnit.DAYS)
    return addDays(date, window.value);
  if (window.unit === ExerciseWindowUnit.YEARS)
    return addYears(date, window.value);
  return addMonths(date, window.value);
}

function subtractUnit(
  date: Date,
  value: number,
  unit: ExerciseWindowUnit
): Date {
  if (unit === ExerciseWindowUnit.DAYS) return addDays(date, -value);
  if (unit === ExerciseWindowUnit.YEARS) return addYears(date, -value);
  return addMonths(date, -value);
}

interface DualDateGuardProps {
  settings: ForfeitureSettings;
  onChange: (val: DualDateGuard) => void;
  disabled?: boolean;
}

function DualDateGuardSection({
  settings,
  onChange,
  disabled,
}: DualDateGuardProps) {
  const [showExample, setShowExample] = useState(false);
  const basis = settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE;
  const basisLabel =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? "Resignation Date"
      : "Last Working Date";
  const otherLabel =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? "Last Working Date"
      : "Resignation Date";
  const guard = settings.dualDateGuard;
  const resolution = guard?.resolution ?? "LATER_DATE";

  const resignationRule = settings.exitRules.find(
    (r) => r.exitType === EmployeeExitType.RESIGNATION
  );

  // Pick a sample tenure that clearly falls INSIDE the first tenure-based
  // conditional's band (if any) — the midpoint, not the boundary, so the
  // example doesn't read as an ambiguous edge case.
  const exampleConditional = resignationRule?.conditionalRules?.[0];
  const exampleTenureValue = exampleConditional?.tenure
    ? exampleConditional.tenure.maxMonths !== undefined
      ? Math.round(
          (exampleConditional.tenure.minMonths +
            exampleConditional.tenure.maxMonths) /
            2
        )
      : // unbounded upper end — pick a value safely past the floor
        exampleConditional.tenure.minMonths + 6
    : undefined;
  const exampleTenureUnit =
    exampleConditional?.tenure?.unit ?? ExerciseWindowUnit.MONTHS;

  // Illustrative-only sample dates (typical 2-week notice period).
  const sampleResignationDate = new Date();
  const sampleLastWorkingDate = addDays(sampleResignationDate, 14);
  const sampleDateOfJoining =
    exampleTenureValue !== undefined
      ? subtractUnit(
          sampleLastWorkingDate,
          exampleTenureValue,
          exampleTenureUnit
        )
      : sampleLastWorkingDate;
  const sampleBasisDate =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? sampleResignationDate
      : sampleLastWorkingDate;
  const sampleOtherDate =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? sampleLastWorkingDate
      : sampleResignationDate;

  // Reuse the same resolver used for real employees — it already applies
  // tenure conditionals and the dual-date guard, so the example can't drift
  // out of sync with the actual computation.
  const resolvedExample = resignationRule
    ? resolveExitOutcome(
        "RESIGNATION",
        sampleBasisDate,
        {
          dateOfJoining: sampleDateOfJoining,
          lastDay: sampleLastWorkingDate,
          resignationDate: sampleResignationDate,
        },
        settings
      )
    : null;

  const exampleWindow = resolvedExample?.vestedOptions.exerciseWindow;
  const isTenureApplied = resolvedExample?.appliedRule.vested === "CONDITIONAL";

  const canShowExample = !!exampleWindow && !exampleWindow.neverExpires;
  const primaryExample = canShowExample
    ? addWindow(sampleBasisDate, exampleWindow!)
    : null;
  // resolvedExpiryDate already has the dual-date guard applied.
  const finalExample = resolvedExample?.resolvedExpiryDate ?? null;

  return (
    <div className="rounded-lg border border-gray-200 p-5 bg-white">
      <VStack className="gap-4">
        <HStack className="items-center justify-between gap-4 w-full">
          <VStack className="gap-0.5">
            <span className="text-sm font-semibold text-gray-900">
              Enable Earlier/Later of Two Dates
            </span>
            <span className="text-xs text-gray-500">
              Use whichever of Resignation Date or Last Working Date gives the{" "}
              {resolution === "EARLIER_DATE" ? "earlier" : "later"} expiry,
              instead of a single fixed basis.
            </span>
          </VStack>
          <div className="flex items-center shrink-0">
            <SwitchButton
              value={!!guard?.enabled}
              disabled={disabled}
              label=""
              onClick={() =>
                onChange({
                  enabled: !guard?.enabled,
                  resolution: guard?.resolution ?? "LATER_DATE",
                })
              }
            />
          </div>
        </HStack>

        {guard?.enabled && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(
                [
                  {
                    value: "EARLIER_DATE",
                    label: "Use the earlier date",
                    description:
                      "Whichever expiry comes sooner wins — more conservative.",
                  },
                  {
                    value: "LATER_DATE",
                    label: "Use the later date",
                    description:
                      "Whichever expiry comes later wins — more generous to the employee.",
                  },
                ] as {
                  value: "EARLIER_DATE" | "LATER_DATE";
                  label: string;
                  description: string;
                }[]
              ).map((opt) => {
                const isActive = resolution === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={disabled}
                    onClick={() =>
                      onChange({ enabled: true, resolution: opt.value })
                    }
                    className={cn(
                      "flex items-start gap-3 text-left rounded-lg border p-4 transition-all duration-200",
                      isActive
                        ? "border-orange-501 bg-orange-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300",
                      disabled && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <div
                      className={cn(
                        "mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                        isActive ? "border-orange-501" : "border-gray-300"
                      )}
                    >
                      {isActive && (
                        <div className="w-2 h-2 rounded-full bg-orange-501" />
                      )}
                    </div>
                    <VStack className="gap-0.5">
                      <span className="text-sm font-semibold text-gray-900">
                        {opt.label}
                      </span>
                      <span className="text-xs text-gray-500">
                        {opt.description}
                      </span>
                    </VStack>
                  </button>
                );
              })}
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowExample((s) => !s)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                <Icon
                  icon="mdi:chevron-right"
                  width={14}
                  className={cn(
                    "transition-transform duration-150",
                    showExample && "rotate-90"
                  )}
                />
                {showExample ? "Hide example" : "Show example"}
              </button>

              {showExample && (
                <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600">
                  {!canShowExample || !primaryExample || !finalExample ? (
                    <p>
                      The Resignation exit rule is set to &quot;never
                      expires&quot;, so there is no computed date to compare
                      against.
                    </p>
                  ) : (
                    <VStack className="gap-2">
                      <p className="italic text-gray-500">
                        Assumed values, for illustration only:
                      </p>
                      <table className="text-left">
                        <tbody>
                          {exampleTenureValue !== undefined && (
                            <tr>
                              <td className="pr-4 py-0.5 text-gray-500">
                                Tenure
                              </td>
                              <td className="font-mono font-medium text-gray-800">
                                {exampleTenureValue}{" "}
                                {exampleTenureUnit.toLowerCase()}
                                {isTenureApplied && (
                                  <span className="ml-1 font-sans font-normal text-orange-600">
                                    (tenure rule applied)
                                  </span>
                                )}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="pr-4 py-0.5 text-gray-500">
                              Exercise Window
                            </td>
                            <td className="font-mono font-medium text-gray-800">
                              {exampleWindow!.value}{" "}
                              {exampleWindow!.unit.toLowerCase()}
                            </td>
                          </tr>
                          <tr>
                            <td className="pr-4 py-0.5 text-gray-500">
                              Resignation Date
                            </td>
                            <td className="font-mono font-medium text-gray-800">
                              {format(sampleResignationDate, "dd MMM yyyy")}
                            </td>
                          </tr>
                          <tr>
                            <td className="pr-4 py-0.5 text-gray-500">
                              Last Working Date
                            </td>
                            <td className="font-mono font-medium text-gray-800">
                              {format(sampleLastWorkingDate, "dd MMM yyyy")}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="text-gray-400">
                        Last Working Date is always on or after Resignation Date
                        — it can never come first.
                      </p>

                      <VStack className="gap-1 pt-2 border-t border-gray-200 font-mono">
                        <p>
                          {basisLabel} + exercise window ={" "}
                          <span className="font-semibold">
                            {format(primaryExample, "dd MMM yyyy")}
                          </span>
                        </p>
                        <p>
                          {otherLabel} (no offset) ={" "}
                          <span className="font-semibold">
                            {format(sampleOtherDate, "dd MMM yyyy")}
                          </span>
                        </p>
                        <p className="text-orange-600">
                          → Final expiry (
                          {resolution === "EARLIER_DATE" ? "earlier" : "later"})
                          = {format(finalExample, "dd MMM yyyy")}
                        </p>
                      </VStack>
                    </VStack>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </VStack>
    </div>
  );
}

// ─── Sub-component: Active Employee Expiry section ────────────────────────────

interface ActiveExpiryProps {
  value: ActiveEmployeeExpiry;
  onChange: (val: ActiveEmployeeExpiry) => void;
  disabled?: boolean;
}

function ActiveEmployeeExpirySection({
  value,
  onChange,
  disabled,
}: ActiveExpiryProps) {
  const active = value.expiryMode;
  const activeOpt = EXPIRY_MODE_OPTIONS.find((o) => o.value === active);

  return (
    <VStack className="gap-4">
      <div>
        <p className="text-lg font-medium mb-1">Active Employee Expiry</p>
        <p className="text-xs text-gray-500">
          When do grants expire for employees currently active in the company?
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {EXPIRY_MODE_OPTIONS.map((opt) => {
          const isActive = active === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...value,
                  expiryMode: opt.value,
                  expiryValue:
                    opt.value === ExpiryMode.NEVER ||
                    opt.value === ExpiryMode.SPECIFIC_DATE
                      ? undefined
                      : value.expiryValue ?? 12,
                  expiryUnit:
                    opt.value === ExpiryMode.NEVER ||
                    opt.value === ExpiryMode.SPECIFIC_DATE
                      ? undefined
                      : value.expiryUnit ?? ExerciseWindowUnit.MONTHS,
                  specificDate:
                    opt.value === ExpiryMode.SPECIFIC_DATE
                      ? value.specificDate
                      : undefined,
                })
              }
              className={cn(
                "relative flex flex-col rounded-lg border p-4 text-left cursor-pointer transition-all duration-200",
                isActive
                  ? "border-orange-501 bg-orange-50 shadow-sm"
                  : "border-gray-200 bg-white hover:border-gray-300",
                disabled && "cursor-not-allowed opacity-60"
              )}
            >
              <div className="flex justify-between items-start mb-3">
                <div
                  className={cn(
                    "w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0",
                    isActive ? "border-orange-501" : "border-gray-300"
                  )}
                >
                  {isActive && (
                    <div className="w-2 h-2 rounded-full bg-orange-501" />
                  )}
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-900 mb-1">
                {opt.label}
              </span>
              <span className="text-xs text-gray-500 leading-relaxed">
                {opt.description}
              </span>
            </button>
          );
        })}
      </div>

      {/* Inline duration control */}
      {activeOpt?.showWindow && (
        <HStack className="items-center gap-3 pt-1">
          <span className="text-sm font-medium text-gray-700">
            Expires after
          </span>
          <HStack className="items-center rounded-lg bg-gray-50 border border-gray-200 overflow-hidden">
            <input
              type="number"
              min="1"
              disabled={disabled}
              value={value.expiryValue ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  expiryValue:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              className="w-20 text-center text-sm font-medium h-10 bg-transparent border-none outline-none px-3"
            />
            <div className="w-px h-6 bg-gray-200 shrink-0" />
            <Select
              disabled={disabled}
              options={UNIT_OPTIONS}
              valueGetter={(o) => o.value}
              textGetter={(o) => o.text}
              value={value.expiryUnit ?? ExerciseWindowUnit.MONTHS}
              onChange={(e: any) =>
                onChange({
                  ...value,
                  expiryUnit: e.target.value as ExerciseWindowUnit,
                })
              }
              className="border-none bg-transparent text-sm h-10 pr-8 pl-3"
            />
          </HStack>
          <span className="text-xs text-gray-400">
            from{" "}
            {activeOpt.value === ExpiryMode.GRANT_EXPIRY
              ? "grant date"
              : "vesting date"}
          </span>
        </HStack>
      )}

      {activeOpt?.showDate && (
        <HStack className="items-center gap-3 pt-1">
          <span className="text-sm font-medium text-gray-700">Expires on</span>
          <HStack className="items-center rounded-lg bg-gray-50 border border-gray-200 overflow-hidden">
            <input
              type="date"
              disabled={disabled}
              value={value.specificDate ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  specificDate: e.target.value || undefined,
                })
              }
              className="text-sm font-medium h-10 bg-transparent border-none outline-none px-3"
            />
          </HStack>
        </HStack>
      )}
    </VStack>
  );
}

// ─── Helper: plain-English exit rule summary ──────────────────────────────────

function describeTenureBand(tenure?: {
  minMonths: number;
  maxMonths?: number;
  unit?: ExerciseWindowUnit;
}): string {
  if (!tenure) return "Regardless of tenure";
  const u = tenureUnitLabel(tenure.unit);
  if (tenure.minMonths === 0 && tenure.maxMonths !== undefined)
    return `With less than ${tenure.maxMonths} ${u} of service`;
  if (tenure.maxMonths !== undefined)
    return `With ${tenure.minMonths}–${tenure.maxMonths} ${u} of service`;
  return `With ${tenure.minMonths}+ ${u} of service`;
}

interface ExitRuleSummaryProps {
  rule: ExitForfeitureRule;
  exitDateBasis: ExitDateBasis;
}

export function ExitRuleSummary({ rule, exitDateBasis }: ExitRuleSummaryProps) {
  const dateLabel =
    exitDateBasis === ExitDateBasis.RESIGNATION_DATE
      ? "resignation date"
      : "last working date";
  const ew = rule.vestedOptions.exerciseWindow;

  const vestedStr = ew.neverExpires
    ? "Vested options never expire."
    : `Vested options can be exercised within ${ew.value ?? 0} ${(
        ew.unit ?? "days"
      ).toLowerCase()} of the ${dateLabel}.`;

  const acc = rule.unvestedOptions.acceleration;
  const unvestedStr =
    acc?.enabled && (acc.percentage ?? 0) > 0
      ? `${acc.percentage}% of unvested options vest immediately on exit.`
      : "Unvested options lapse immediately on exit.";

  const conditionalRules = rule.conditionalRules ?? [];

  return (
    <div className="space-y-1.5">
      <p>
        {vestedStr} {unvestedStr}
      </p>
      {conditionalRules.length > 0 && (
        <>
          <p className="font-medium mt-2">
            {conditionalRules.length} tenure-based{" "}
            {conditionalRules.length === 1
              ? "condition overrides"
              : "conditions override"}{" "}
            the vested exercise window:
          </p>
          {conditionalRules.map((cr, i) => {
            const tenureLabel = describeTenureBand(cr.tenure);
            const crEw = cr.vestedOptions?.exerciseWindow;
            const crVested = crEw?.neverExpires
              ? "vested options never expire"
              : crEw
              ? `${crEw.value ?? 0} ${(
                  crEw.unit ?? "days"
                ).toLowerCase()} exercise window`
              : "default window";
            return (
              <p key={i} className="flex items-start gap-1.5">
                <span className="shrink-0">•</span>
                <span>
                  <span className="font-medium">Condition {i + 1}</span> (
                  {tenureLabel}): {crVested}.
                </span>
              </p>
            );
          })}
          {conditionalRules.length >= 2 && (
            <p className="mt-1">
              Conflict policy:{" "}
              <span className="font-medium">
                {(rule.conditionalResolution ?? "EARLIER_DATE") ===
                "EARLIER_DATE"
                  ? "Earlier exercise window takes precedence."
                  : "Later exercise window takes precedence."}
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Sub-component: Single Exit Rule card ─────────────────────────────────────

interface ExitRuleCardProps {
  rule: ExitForfeitureRule;
  onChange: (rule: ExitForfeitureRule) => void;
  onRemove: () => void;
  onApplyToAll?: () => void;
  showApplyToAll?: boolean;
  exitDateBasis: ExitDateBasis;
  disabled?: boolean;
}

function ExitRuleCard({
  rule,
  onChange,
  onRemove,
  onApplyToAll,
  showApplyToAll,
  exitDateBasis,
  disabled,
}: ExitRuleCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showConditional, setShowConditional] = useState(
    (rule.conditionalRules?.length ?? 0) > 0
  );

  const { vestedOptions, unvestedOptions } = rule;
  const ew = vestedOptions.exerciseWindow;

  const exitDateBasisLabel =
    exitDateBasis === ExitDateBasis.RESIGNATION_DATE
      ? "resignation date"
      : "last working date";

  const conditionalRules = rule.conditionalRules ?? [];
  const conditionalRuleSummary = getConditionalRuleSummary(rule);

  const vestedPreview = ew.neverExpires
    ? "Never Expires"
    : `${ew.value ?? 0} ${(ew.unit ?? "").toLowerCase()}`;

  const unvestedPreview = unvestedOptions.acceleration?.enabled
    ? `${unvestedOptions.acceleration.percentage}% Acceleration`
    : "Immediate Lapse";

  const addConditionalRule = () => {
    setShowConditional(true);
    onChange({
      ...rule,
      conditionalRules: [...conditionalRules, defaultConditionalRule()],
    });
  };

  const removeConditionalRule = (i: number) =>
    onChange({
      ...rule,
      conditionalRules: conditionalRules.filter((_, idx) => idx !== i),
    });

  const updateConditionalRule = (
    i: number,
    updated: NonNullable<ExitForfeitureRule["conditionalRules"]>[number]
  ) =>
    onChange({
      ...rule,
      conditionalRules: conditionalRules.map((cr, idx) =>
        idx === i ? updated : cr
      ),
    });

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Accordion header */}
      <div
        className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer select-none"
        onClick={() => setIsOpen((v) => !v)}
      >
        <div className="flex-1 min-w-0">
          <HStack className="gap-3 items-center">
            <span className="text-sm font-semibold text-gray-900">
              {EXIT_TYPE_LABELS[rule.exitType]}
            </span>
            {conditionalRules.length > 0 && (
              <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-600">
                {conditionalRules.length}{" "}
                {conditionalRules.length === 1 ? "condition" : "conditions"}
              </span>
            )}
          </HStack>
          <HStack className="gap-0 text-xs text-gray-400 mt-1 divide-x divide-gray-300 flex-wrap">
            <span className="pr-2">Vested: {vestedPreview}</span>
            <span className="px-2">Unvested: {unvestedPreview}</span>
            {conditionalRules.length > 0 && (
              <span className="px-2">{conditionalRuleSummary}</span>
            )}
          </HStack>
        </div>

        <HStack className="items-center gap-3 shrink-0 ml-4">
          {showApplyToAll && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onApplyToAll?.();
              }}
              className="text-xs font-semibold text-orange-501 hover:underline"
            >
              Apply same to all
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Remove rule"
            >
              <Icon icon="mdi:close" width={16} />
            </button>
          )}
          <Icon
            icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
            width={20}
            className="text-gray-400"
          />
        </HStack>
      </div>

      {isOpen && (
        <>
          {/* Blue plain-English summary */}
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <Icon
              icon="mdi:information-outline"
              width={14}
              className="text-blue-400 shrink-0 mt-0.5"
            />
            <div className="text-xs text-blue-700 leading-relaxed">
              <ExitRuleSummary rule={rule} exitDateBasis={exitDateBasis} />
            </div>
          </div>

          {/* Base Rule */}
          {conditionalRules.length > 0 && (
            <div className="text-xs text-gray-400 italic mt-3 px-5">
              The base rule applies to all employees not matching any condition.
            </div>
          )}
          <div className="border-b border-gray-100">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 bg-gray-50 p-4">
              {/* Vested Options Card */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold text-gray-500 mb-4">
                  Vested Options
                </p>

                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-gray-700">
                    Never Expires
                  </span>
                  <SwitchButton
                    label=""
                    value={ew.neverExpires ?? false}
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        ...rule,
                        vestedOptions: {
                          ...vestedOptions,
                          exerciseWindow: {
                            ...ew,
                            neverExpires: !ew.neverExpires,
                            value: ew.neverExpires ? ew.value || 90 : ew.value,
                          },
                        },
                      })
                    }
                  />
                </div>

                {!ew.neverExpires && (
                  <HStack className="items-center gap-3 mt-3">
                    <span className="text-sm text-gray-500 shrink-0">
                      Window:
                    </span>
                    <ExerciseWindowPill
                      window={ew}
                      onChange={(newEw) =>
                        onChange({
                          ...rule,
                          vestedOptions: {
                            ...vestedOptions,
                            exerciseWindow: newEw,
                          },
                        })
                      }
                      disabled={disabled}
                    />
                  </HStack>
                )}

                {!ew.neverExpires && (
                  <p className="text-xs text-gray-400 italic mt-3">
                    {`Vested options expire ${ew.value ?? 0} ${(
                      ew.unit ?? ""
                    ).toLowerCase()} after ${exitDateBasisLabel}.`}
                  </p>
                )}
              </div>

              {/* Unvested Options Card */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold text-gray-500 mb-4">
                  Unvested Options
                </p>
                <UnvestedOptionsFields
                  value={unvestedOptions}
                  onChange={(u) => onChange({ ...rule, unvestedOptions: u })}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>

          {/* Conditional Rules */}
          <div className="bg-white">
            <button
              type="button"
              onClick={() => setShowConditional((v) => !v)}
              className="w-full px-5 py-4 text-left transition-colors hover:bg-gray-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Tenure-based overrides
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Add exceptions only when this exit type needs a different
                    rule for specific tenure bands.
                  </p>
                </div>
                <HStack className="items-center gap-2 shrink-0">
                  {!disabled && conditionalRules.length === 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        addConditionalRule();
                      }}
                      className="rounded-lg border border-dashed border-orange-300 px-3 py-1.5 text-xs font-semibold text-orange-600 transition-colors hover:bg-orange-50"
                    >
                      Add tenure-based override
                    </button>
                  )}
                  {conditionalRules.length > 0 && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                      {conditionalRules.length}
                    </span>
                  )}
                  <Icon
                    icon={
                      showConditional ? "mdi:chevron-up" : "mdi:chevron-down"
                    }
                    width={20}
                    className="text-gray-400"
                  />
                </HStack>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {conditionalRuleSummary}
              </p>
            </button>

            {showConditional && (
              <VStack className="gap-4 border-t border-gray-100 px-5 py-5">
                {conditionalRules.length === 0 && (
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-5">
                    <p className="text-sm font-medium text-gray-700">
                      No overrides yet
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Start by adding a tenure band, then override the vested
                      exercise window for that band.
                    </p>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={addConditionalRule}
                        className="mt-4 rounded-lg border border-dashed border-orange-300 px-4 py-2 text-xs font-semibold text-orange-600 transition-colors hover:bg-orange-50"
                      >
                        Add first tenure-based override
                      </button>
                    )}
                  </div>
                )}

                {conditionalRules.map((cr, i) => (
                  <ConditionalRuleCard
                    key={i}
                    rule={cr}
                    index={i}
                    onChange={(updated) => updateConditionalRule(i, updated)}
                    onRemove={() => removeConditionalRule(i)}
                    disabled={disabled}
                  />
                ))}

                {conditionalRules.length >= 2 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold text-gray-500 mb-3">
                      When multiple tenure conditions match the same employee
                    </p>
                    <HStack className="gap-6 flex-wrap">
                      {[
                        {
                          value: "EARLIER_DATE" as const,
                          label: "Whichever exercise window ends first",
                        },
                        {
                          value: "LATER_DATE" as const,
                          label: "Whichever exercise window ends last",
                        },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          <input
                            type="radio"
                            disabled={disabled}
                            checked={
                              (rule.conditionalResolution ?? "EARLIER_DATE") ===
                              opt.value
                            }
                            onChange={() =>
                              onChange({
                                ...rule,
                                conditionalResolution: opt.value,
                              })
                            }
                            className="text-orange-500"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </HStack>
                  </div>
                )}

                {!disabled && conditionalRules.length > 0 && (
                  <button
                    type="button"
                    onClick={addConditionalRule}
                    className="self-start rounded-lg border border-dashed border-orange-300 px-4 py-2 text-xs font-semibold text-orange-600 transition-colors hover:bg-orange-50"
                  >
                    + Add another tenure-based override
                  </button>
                )}
              </VStack>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main ForfeitureSettingsForm ──────────────────────────────────────────────

interface ForfeitureSettingsFormProps {
  value?: ForfeitureSettings;
  onChange?: (val: ForfeitureSettings) => void;
  disabled?: boolean;
}

export function ForfeitureSettingsForm({
  value: controlledValue,
  onChange: controlledOnChange,
  disabled,
}: ForfeitureSettingsFormProps) {
  const formik = (() => {
    try {
      return useFormikContext<AddPlanReq>();
    } catch {
      return null;
    }
  })();

  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  const settings: ForfeitureSettings = controlledValue ??
    formik?.values.forfeitureSettings ?? {
      activeEmployee: { expiryMode: ExpiryMode.GRANT_EXPIRY },
      exitRules: [],
    };

  const setSettings = (val: ForfeitureSettings) => {
    if (controlledOnChange) {
      controlledOnChange(val);
    } else {
      formik?.setFieldValue("forfeitureSettings", val);
    }
  };

  const applyFirstRuleToAll = (baseRule: ExitForfeitureRule) => {
    setSettings({
      ...settings,
      exitRules: settings.exitRules.map((r) => ({
        ...r,
        vestedOptions: { ...baseRule.vestedOptions },
        unvestedOptions: { ...baseRule.unvestedOptions },
        conditionalRules: baseRule.conditionalRules
          ? baseRule.conditionalRules.map((cr) => ({ ...cr }))
          : undefined,
      })),
    });
  };

  const addedExitTypes = new Set(settings.exitRules.map((r) => r.exitType));
  const unaddedExitTypes = ALL_EXIT_TYPES.filter((t) => !addedExitTypes.has(t));

  const addAllRules = () =>
    setSettings({
      ...settings,
      exitRules: [
        ...settings.exitRules,
        ...unaddedExitTypes.map(defaultExitRule),
      ],
    });

  const addRule = (exitType: EmployeeExitType) =>
    setSettings({
      ...settings,
      exitRules: [...settings.exitRules, defaultExitRule(exitType)],
    });

  const removeRule = (exitType: EmployeeExitType) =>
    setSettings({
      ...settings,
      exitRules: settings.exitRules.filter((r) => r.exitType !== exitType),
    });

  const updateRule = (
    exitType: EmployeeExitType,
    updated: ExitForfeitureRule
  ) =>
    setSettings({
      ...settings,
      exitRules: settings.exitRules.map((r) =>
        r.exitType === exitType ? updated : r
      ),
    });

  return (
    <VStack className="gap-8 py-6 pr-7">
      {!disabled && (
        <HStack className="justify-between items-center rounded-lg border border-orange-200 bg-orange-50/50 p-5 gap-4">
          <VStack className="gap-1.5">
            <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <Icon
                icon="mdi:sparkles"
                className="text-orange-500"
                width={18}
              />
              Generate Forfeiture Settings with AI
            </span>
            <span className="text-xs text-gray-500 leading-relaxed">
              Paste your scheme rules policy text or upload a DOCX file to
              automatically detect and configure exit rules, exercise windows,
              and tenure overrides.
            </span>
          </VStack>
          <button
            type="button"
            onClick={() => setAiDialogOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm shrink-0 animate-pulse hover:animate-none"
          >
            <Icon icon="mdi:sparkles" width={16} />
            Generate with AI
          </button>
        </HStack>
      )}

      {aiDialogOpen && (
        <ForfeitureAIDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          onApply={(json) => {
            setSettings(json);
            setAiDialogOpen(false);
          }}
        />
      )}

      {/* Section 1: Active Employee Expiry */}
      <div className="rounded-lg border border-gray-200 p-5 bg-white">
        <ActiveEmployeeExpirySection
          value={settings.activeEmployee}
          onChange={(activeEmployee) =>
            setSettings({ ...settings, activeEmployee })
          }
          disabled={disabled}
        />
      </div>

      {/* Section 2: Exit Date Basis */}
      <div className="rounded-lg border border-gray-200 p-5 bg-white">
        <VStack className="gap-4">
          <div>
            <p className="text-lg font-medium mb-1">Exit Date Basis</p>
            <p className="text-xs text-gray-500">
              Which date starts the exercise window when an employee exits.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(
              [
                {
                  value: ExitDateBasis.LAST_WORKING_DATE,
                  label: "Last Working Date",
                  description:
                    "Exercise window starts from the employee's last working day.",
                },
                {
                  value: ExitDateBasis.RESIGNATION_DATE,
                  label: "Resignation Date",
                  description:
                    "Exercise window starts from the date resignation was submitted.",
                },
              ] as {
                value: ExitDateBasis;
                label: string;
                description: string;
              }[]
            ).map((opt) => {
              const isActive =
                (settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE) ===
                opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    setSettings({ ...settings, exitDateBasis: opt.value })
                  }
                  className={cn(
                    "flex items-start gap-3 text-left rounded-lg border p-4 transition-all duration-200",
                    isActive
                      ? "border-orange-501 bg-orange-50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300",
                    disabled && "cursor-not-allowed opacity-60"
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                      isActive ? "border-orange-501" : "border-gray-300"
                    )}
                  >
                    {isActive && (
                      <div className="w-2 h-2 rounded-full bg-orange-501" />
                    )}
                  </div>
                  <VStack className="gap-0.5">
                    <span className="text-sm font-semibold text-gray-900">
                      {opt.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {opt.description}
                    </span>
                  </VStack>
                </button>
              );
            })}
          </div>
        </VStack>
      </div>

      {(settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE) ===
        ExitDateBasis.RESIGNATION_DATE && (
        <DualDateGuardSection
          settings={settings}
          onChange={(dualDateGuard) =>
            setSettings({ ...settings, dualDateGuard })
          }
          disabled={disabled}
        />
      )}

      {/* Section 3: Exit Rules */}
      <VStack className="gap-4">
        <HStack className="justify-between items-start">
          <div>
            <p className="text-lg font-medium mb-1">Exit Rules</p>
            <p className="text-xs text-gray-500">
              Define what happens to vested and unvested options under each
              separation type.
            </p>
          </div>
          {!disabled && unaddedExitTypes.length > 0 && (
            <button
              type="button"
              onClick={addAllRules}
              className="text-xs font-semibold text-orange-501 hover:underline shrink-0"
            >
              Add All Rules
            </button>
          )}
        </HStack>

        {settings.exitRules.map((rule, index) => (
          <ExitRuleCard
            key={rule.exitType}
            rule={rule}
            onChange={(updated) => updateRule(rule.exitType, updated)}
            onRemove={() => removeRule(rule.exitType)}
            showApplyToAll={index === 0 && settings.exitRules.length > 1}
            onApplyToAll={() => applyFirstRuleToAll(rule)}
            exitDateBasis={
              settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE
            }
            disabled={disabled}
          />
        ))}

        {!disabled && unaddedExitTypes.length > 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">
              {settings.exitRules.length === 0
                ? "Add a rule for each exit type you want to configure:"
                : "Add rules for more exit types:"}
            </p>
            <p className="text-xs text-gray-400 italic mb-3">
              Unadded exit types will default to never expire.
            </p>
            <div className="flex flex-wrap gap-2">
              {unaddedExitTypes.map((exitType) => (
                <button
                  key={exitType}
                  type="button"
                  onClick={() => addRule(exitType)}
                  className="px-4 py-1.5 rounded-full border border-gray-200 text-sm text-gray-600 hover:border-orange-501 hover:text-orange-501 transition-all"
                >
                  + {EXIT_TYPE_LABELS[exitType]}
                </button>
              ))}
            </div>
          </div>
        )}

        {!disabled && settings.exitRules.length === 0 && (
          <p className="text-xs text-gray-400 italic">
            No exit rules configured. Default is total forfeiture on exit.
          </p>
        )}
      </VStack>
    </VStack>
  );
}

export default ForfeitureSettingsForm;
